import { yup } from '@framework/validation';
import { has } from 'lodash';
import { v4 as uuid } from 'uuid';
import { IBaseWidget } from '../IBaseWidget';
import type { IWidgetType } from '../IWidgetType';
import { Widget, WidgetType } from '../Widget';

/**
 * Classe che permette di gestire in maniera centralizzata la connessione tra
 * un componente react e il tipo di dato (schema JSON).
 *
 * I componenti sono registrati all'interno della struttura `IWidgetType`
 * nei file `*.type.ts` in cui vengono definite tutte le proprietà necessarie.
 * Forniti i dati di un elemento a livello di `object`, il repository fornirà il
 * componente react necessario per la renderizzazione.
 */
class WidgetRepositoryClass {
  public types: Record<string, IWidgetType<any>> = {};
  public isInitialized = false;

  register<T extends IBaseWidget>(widgetType: IWidgetType<T>) {
    if (has(this.types, widgetType.type)) {
      const error = new Error(
        `Impossibile registrare un Tipo  di Widget con tipo: "${widgetType.type}" poiché è già registrato`,
      );

      if (process.env.NODE_ENV === 'development') {
        console.warn(error.message);
        return;
      }

      throw error;
    }

    this.types[widgetType.type] = widgetType;
  }

  find<T extends IBaseWidget = Widget>(code: string | null | undefined) {
    if (code == null) return null;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
    if (code === WidgetType.Root) return null;

    if (!has(this.types, code)) {
      console.error(`Impossibile ottenere il Tipo di Widget con codice: "${code}" poiché non è registrato`); // prettier-ignore
    }

    return this.types[code] as IWidgetType<T> | null;
  }

  listAll() {
    return Object.values(this.types);
  }

  getConfigValidationSchema(
    widget: Widget | null | undefined,
    defaultSchema: any = yup.object(),
  ) {
    if (!widget) return null;
    const item = this.find(widget.type);

    const fieldsValidator =
      item?.configValidator ?? (defaultSchema as yup.AnyObjectSchema);

    // TODO Perché qui aggiungiamo `noUnknown` se invece in `LazyWidgetSchema` non lo aggiungiamo? @Khaos101
    const result = fieldsValidator.noUnknown().meta({ widget: widget.type });

    return result as yup.ObjectSchema<Widget>;
  }

  getConfigComponent(widget: Widget) {
    const item = this.find(widget.type);
    if (!item?.configComponent) {
      console.warn(`[widgetConfigEditor] Nessun componente di configurazioneper il widget ${widget.type}`, {widget}); // prettier-ignore
      throw new Error(`Nessuno componente di validazione per il widget ${widget.type}`); // prettier-ignore
    }
    return item.configComponent;
  }

  getInitial(
    widgetType: WidgetType,
    partialWidget?: Partial<Widget>,
  ): Widget | null {
    const widget = this.find(widgetType);

    if (!widget) {
      console.warn(`[widgetConfigEditor] Nessun componente di configurazione per il widget ${widgetType}`, {widgetType}); // prettier-ignore
      return null;
    }
    const result: Widget | null =
      widget.configValidator?.cast(
        {
          // Imposto l'UUID del widget appena creato
          id: uuid(),
          // Imposto una label/titolo di default (i campi verrano rimossi se non necessari)
          label: [{ type: 'text', text: `Nuovo elemento ${widget.name}` }],
          title: [{ type: 'text', text: `Nuovo elemento ${widget.name}` }],
          // Passo tutti i valori di default forniti alla funzione
          ...partialWidget,
        },
        { assert: false },
      ) ?? null;

    return result;
  }

  /**
   * Ritorna il tipo di dato del widget fornito.
   */
  getFieldDataType(widgetType: IWidgetType<any>, widget: IBaseWidget) {
    const field = widgetType.getReference?.([], widget);
    return field?.field.type;
  }
}

/**
 * L'inizializzazione dei tipi di Widget avviene in fase di inizializzazione
 * in `initWidgetRepository.ts`.
 * Questo script viene chiamato:
 * - In fase di inizializzazione dell'applicazione, quando viene importato
 *   `@framework/engine`. In questo modo i tipi di widget sono disponibili
 * - Per i test Jest, in `initJestWidgetRepository.ts`
 */
export const WidgetRepository = new WidgetRepositoryClass();
